import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import { waitlistAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { Logo } from './Logo';
import {
  Scissors,
  Clock,
  Users,
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
  Bell,
  BellOff,
  Loader2,
  Sparkles,
  XCircle,
  MessageSquare,
} from 'lucide-react';

const POLL_INTERVAL_MS = 15000;

const formatWait = (minutes) => {
  if (minutes === 0) return 'No wait';
  if (minutes < 60) return `~${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `~${h} hr` : `~${h} hr ${m} min`;
};

const formatCloseTime = (isoString) => {
  if (!isoString) return null;
  const d = new Date(isoString);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const showNotification = (title, body) => {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission === 'granted') {
    try {
      new Notification(title, {
        body,
        icon: '/rooneys-qr-simple.png',
        badge: '/rooneys-qr-simple.png',
        tag: 'rooneys-waitlist',
        renotify: true,
      });
    } catch (e) {
      // Ignore failures (e.g., constructor not allowed on some browsers)
    }
  }
};

const playChime = () => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(880, ctx.currentTime);
    o.frequency.setValueAtTime(1320, ctx.currentTime + 0.15);
    g.gain.setValueAtTime(0.15, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
    o.connect(g);
    g.connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.6);
  } catch (e) {
    // ignore
  }
};

export const MyStatusPage = () => {
  const { id } = useParams();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLeaving, setIsLeaving] = useState(false);
  const [messageDraft, setMessageDraft] = useState('');
  const [isEditingMessage, setIsEditingMessage] = useState(false);
  const [isSavingMessage, setIsSavingMessage] = useState(false);
  const [notifPermission, setNotifPermission] = useState(
    typeof window !== 'undefined' && 'Notification' in window
      ? Notification.permission
      : 'unsupported'
  );

  // Track previous position to detect changes
  const prevPositionRef = useRef(null);
  const prevStatusRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const result = await waitlistAPI.getMyStatus(id);
      setData(result);
      setError(null);

      // Notification triggers (based on changes from prev state)
      const prevPos = prevPositionRef.current;
      const prevStatus = prevStatusRef.current;

      if (result.status === 'waiting' && prevStatus === 'waiting' && prevPos !== null) {
        if (prevPos > 1 && result.position === 1) {
          showNotification(
            "You're up next at Rooney's!",
            "Head over to the shop now."
          );
          playChime();
        } else if (prevPos > 2 && result.position === 2) {
          showNotification(
            "Almost your turn at Rooney's",
            "You're 2 customers away. Get ready!"
          );
          playChime();
        } else if (prevPos !== result.position) {
          showNotification(
            "Line moved at Rooney's",
            `You're now position #${result.position} for ${result.barber}.`
          );
        }
      }

      if (result.status === 'served' && prevStatus === 'waiting') {
        showNotification(
          "You're up at Rooney's!",
          "Your barber is ready for you."
        );
        playChime();
      }

      prevPositionRef.current = result.position;
      prevStatusRef.current = result.status;
    } catch (err) {
      if (err.response?.status === 404) {
        setError('not_found');
      } else {
        setError('error');
      }
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const handleLeave = async () => {
    setIsLeaving(true);
    try {
      await waitlistAPI.leave(id);
      toast({
        title: 'Removed from waitlist',
        description: "You've been taken off the list. Come back anytime!",
      });
      await fetchStatus();
    } catch (err) {
      toast({
        title: 'Could not remove',
        description: err.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLeaving(false);
    }
  };

  const handleSaveMessage = async () => {
    setIsSavingMessage(true);
    try {
      await waitlistAPI.updateMessage(id, messageDraft);
      toast({
        title: 'Note sent to shop',
        description: 'The staff will see your note.',
      });
      setIsEditingMessage(false);
      await fetchStatus();
    } catch (err) {
      toast({
        title: 'Could not save',
        description: err.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSavingMessage(false);
    }
  };

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      toast({
        title: 'Notifications not supported',
        description: "Your browser doesn't support notifications.",
        variant: 'destructive',
      });
      return;
    }
    try {
      const permission = await Notification.requestPermission();
      setNotifPermission(permission);
      if (permission === 'granted') {
        toast({
          title: 'Notifications enabled!',
          description: "We'll alert you when it's almost your turn.",
        });
        showNotification("You're all set!", "We'll notify you when your turn is close.");
      }
    } catch (err) {
      // Permissions API not available or denied
    }
  };

  // ===== Loading =====
  if (isLoading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="h-10 w-10 text-stone-400 animate-spin" />
      </div>
    );
  }

  // ===== Not Found =====
  if (error === 'not_found') {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full bg-white border-stone-200">
          <CardContent className="p-10 text-center space-y-4">
            <AlertCircle className="h-12 w-12 text-stone-400 mx-auto" />
            <h2 className="text-2xl font-bold text-stone-900">Status not found</h2>
            <p className="text-stone-600">
              This waitlist entry may have expired or been removed.
            </p>
            <Link to="/">
              <Button className="bg-maroon-800 hover:bg-maroon-900 text-stone-100">
                Back to homepage
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
        <p className="text-stone-600">Couldn't load your status. Please try refreshing.</p>
      </div>
    );
  }

  const { status, barber, service, party_size, position, people_ahead, estimated_wait_minutes, past_closing, shop } = data;
  const closesAt = formatCloseTime(shop?.closes_at);

  // ===== Served =====
  if (status === 'served') {
    return (
      <div className="min-h-screen bg-stone-50">
        <Header />
        <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card className="bg-white border-stone-200 shadow-lg">
            <CardContent className="p-10 text-center space-y-5">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-12 w-12 text-green-700" />
              </div>
              <h2 className="text-3xl font-bold text-stone-900">You're all done!</h2>
              <p className="text-stone-600">
                Thanks for visiting Rooney's. We hope to see you again soon.
              </p>
              <Link to="/">
                <Button className="bg-maroon-800 hover:bg-maroon-900 text-stone-100">
                  Back to homepage
                </Button>
              </Link>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // ===== Cancelled =====
  if (status === 'cancelled') {
    return (
      <div className="min-h-screen bg-stone-50">
        <Header />
        <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card className="bg-white border-stone-200 shadow-lg">
            <CardContent className="p-10 text-center space-y-5">
              <div className="w-20 h-20 rounded-full bg-stone-100 flex items-center justify-center mx-auto">
                <XCircle className="h-12 w-12 text-stone-500" />
              </div>
              <h2 className="text-3xl font-bold text-stone-900">You've left the waitlist</h2>
              <p className="text-stone-600">
                No worries — you can rejoin anytime.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                <Link to="/#contact">
                  <Button className="bg-maroon-800 hover:bg-maroon-900 text-stone-100 w-full sm:w-auto">
                    Rejoin Waitlist
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="border-stone-300 w-full sm:w-auto">
                    Back to homepage
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // ===== Expired (previous day) =====
  if (status === 'expired') {
    return (
      <div className="min-h-screen bg-stone-50">
        <Header />
        <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card className="bg-white border-stone-200 shadow-lg">
            <CardContent className="p-10 text-center space-y-5">
              <AlertCircle className="h-12 w-12 text-stone-400 mx-auto" />
              <h2 className="text-2xl font-bold text-stone-900">Your spot has expired</h2>
              <p className="text-stone-600">
                The waitlist resets daily. Come back next time we're open to join again.
              </p>
              <Link to="/#contact">
                <Button className="bg-maroon-800 hover:bg-maroon-900 text-stone-100">
                  Join Today's Waitlist
                </Button>
              </Link>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // ===== Waiting =====
  const isYourTurn = position === 1;
  const isAlmostUp = position === 2;

  return (
    <div className="min-h-screen bg-stone-50">
      <Header />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-stone-600 hover:text-maroon-800 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to homepage
        </Link>

        {/* Hero status card */}
        <Card className={`shadow-lg border-2 ${
          isYourTurn
            ? 'bg-maroon-800 border-maroon-900 text-stone-100'
            : 'bg-white border-stone-200'
        }`} data-testid="status-card">
          <CardContent className="p-8 text-center space-y-4">
            {isYourTurn ? (
              <>
                <div className="w-20 h-20 rounded-full bg-stone-100 flex items-center justify-center mx-auto">
                  <Sparkles className="h-12 w-12 text-maroon-800" />
                </div>
                <h2 className="text-3xl font-bold">You're up next!</h2>
                <p className="text-rose-100">
                  Head to the shop now — {barber} is ready for you.
                </p>
              </>
            ) : (
              <>
                <p className="text-sm text-stone-500 uppercase tracking-wide">Your position</p>
                <p className="text-7xl font-bold text-stone-900" data-testid="position-number">
                  #{position}
                </p>
                <p className="text-stone-600">
                  in line for <span className="font-semibold text-stone-900">{barber}</span>
                </p>
                {isAlmostUp && (
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-maroon-100 text-maroon-900 text-sm font-semibold">
                    <Sparkles className="h-4 w-4" />
                    Almost your turn!
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Details grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="bg-white border-stone-200">
            <CardContent className="p-5 text-center">
              <Users className="h-5 w-5 text-maroon-800 mx-auto mb-2" />
              <p className="text-xs text-stone-500 uppercase tracking-wide">People ahead</p>
              <p className="text-2xl font-bold text-stone-900 mt-1">{people_ahead}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-stone-200">
            <CardContent className="p-5 text-center">
              <Clock className="h-5 w-5 text-maroon-800 mx-auto mb-2" />
              <p className="text-xs text-stone-500 uppercase tracking-wide">Est. wait</p>
              <p className="text-2xl font-bold text-stone-900 mt-1" data-testid="my-wait-time">
                {past_closing
                  ? (shop?.is_open ? 'After hours' : 'Closed')
                  : formatWait(estimated_wait_minutes)}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-white border-stone-200">
            <CardContent className="p-5 text-center">
              <Scissors className="h-5 w-5 text-maroon-800 mx-auto mb-2" />
              <p className="text-xs text-stone-500 uppercase tracking-wide">Service</p>
              <p className="text-sm font-semibold text-stone-900 mt-2 truncate">{service}</p>
              <p className="text-xs text-stone-500">
                {party_size} {party_size === 1 ? 'person' : 'people'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Note to the shop */}
        <Card className="bg-white border-stone-200" data-testid="note-card">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-maroon-800" />
                <h3 className="font-semibold text-stone-900">Note to the shop</h3>
              </div>
              {!isEditingMessage && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setMessageDraft(data.message || '');
                    setIsEditingMessage(true);
                  }}
                  className="border-stone-300 text-stone-700 hover:bg-stone-50"
                  data-testid="edit-note-btn"
                >
                  {data.message ? 'Edit' : 'Add note'}
                </Button>
              )}
            </div>

            {isEditingMessage ? (
              <div className="space-y-3">
                <textarea
                  value={messageDraft}
                  onChange={(e) => setMessageDraft(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="e.g., Running 10 min late, bringing my son too, special request..."
                  className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-maroon-700 focus:border-maroon-700 bg-white resize-none text-sm"
                  data-testid="note-textarea"
                />
                <div className="flex gap-2 justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setIsEditingMessage(false)}
                    className="border-stone-300"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSaveMessage}
                    disabled={isSavingMessage}
                    className="bg-maroon-800 hover:bg-maroon-900 text-stone-100"
                    data-testid="save-note-btn"
                  >
                    {isSavingMessage ? 'Sending...' : 'Send to shop'}
                  </Button>
                </div>
              </div>
            ) : data.message ? (
              <p className="text-sm text-stone-700 bg-amber-50 border border-amber-200 rounded p-3 break-words">
                {data.message}
              </p>
            ) : (
              <p className="text-sm text-stone-500 italic">
                No note yet. Let the shop know if you're running late or have a request.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Past-closing warning */}
        {past_closing && shop?.is_open && (
          <Card className="bg-amber-50 border-amber-300">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900">
                <span className="font-semibold">Heads up:</span> Your turn may extend past closing
                {closesAt ? ` at ${closesAt}` : ''}. Please call ahead to confirm.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Notification permission */}
        {notifPermission === 'default' && (
          <Card className="bg-stone-700 border-stone-600 text-stone-100" data-testid="notif-prompt">
            <CardContent className="p-5 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <Bell className="h-6 w-6 text-maroon-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-semibold">Get notified when you're almost up</p>
                  <p className="text-sm text-stone-400">
                    Free browser alerts — no SMS or signup needed.
                  </p>
                </div>
              </div>
              <Button
                onClick={requestNotificationPermission}
                className="bg-maroon-800 hover:bg-maroon-900 text-stone-100"
                data-testid="enable-notif-btn"
              >
                Enable Alerts
              </Button>
            </CardContent>
          </Card>
        )}

        {notifPermission === 'granted' && (
          <div className="flex items-center justify-center gap-2 text-sm text-green-700">
            <Bell className="h-4 w-4" />
            Notifications on. We'll alert you when it's almost your turn.
          </div>
        )}

        {notifPermission === 'denied' && (
          <div className="flex items-center justify-center gap-2 text-sm text-stone-500">
            <BellOff className="h-4 w-4" />
            Notifications blocked. Keep this page open to see live updates.
          </div>
        )}

        {/* Leave Waitlist */}
        <div className="pt-2 flex justify-center">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                className="border-stone-300 text-stone-600 hover:bg-stone-100 hover:text-stone-900"
                data-testid="leave-waitlist-btn"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Leave Waitlist
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Leave the waitlist?</AlertDialogTitle>
                <AlertDialogDescription>
                  You'll be taken off the list and lose your spot. You can rejoin anytime.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="leave-cancel">Stay in line</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleLeave}
                  disabled={isLeaving}
                  className="bg-stone-700 hover:bg-stone-800 text-stone-100"
                  data-testid="leave-confirm"
                >
                  {isLeaving ? 'Leaving...' : 'Yes, leave waitlist'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        <p className="text-center text-xs text-stone-500 pt-4">
          Updates every 15 seconds. Bookmark this page so you can come back anytime.
        </p>
      </main>
    </div>
  );
};

const Header = () => (
  <header className="bg-stone-700 border-b border-stone-600">
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-3 group">
        <Logo variant="full-light" className="h-12 w-auto" />
        <p className="hidden sm:block text-xs text-stone-400 border-l border-stone-500 pl-3">
          Your Spot
        </p>
      </Link>
    </div>
  </header>
);

export default MyStatusPage;
